import { fetchProductCards } from '@theme/mineralia-product-list';
import { Wishlist, WISHLIST_EVENT } from '@theme/mineralia-wishlist';

/**
 * <mineralia-favorites> renders the saved pieces of the wishlist and keeps the grid in sync
 * when a heart is toggled (also from other tabs).
 */
class MineraliaFavorites extends HTMLElement {
  #abort = new AbortController();
  /** @type {string[]} */
  #rendered = [];

  connectedCallback() {
    if (this.#abort.signal.aborted) this.#abort = new AbortController();
    document.addEventListener(WISHLIST_EVENT, this.#onChange, { signal: this.#abort.signal });
    this.#load();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get #list() {
    return this.querySelector('[data-favorites-list]');
  }

  async #load() {
    const ids = Wishlist.getIds();
    if (ids.length === 0) {
      this.#renderEmpty();
      return;
    }

    try {
      const cards = await fetchProductCards(ids, 'mineralia-favorites', this.#abort.signal);
      const list = this.#list;
      if (!list) return;
      if (cards.length === 0) {
        this.#renderEmpty();
        return;
      }
      list.replaceChildren(...cards);
      list.removeAttribute('aria-busy');
      this.#rendered = cards.map((card) => card.dataset.productCardId || '');
      this.dataset.state = 'ready';
      this.#updateCount();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.warn('[mineralia-favorites]', error);
      this.#renderEmpty();
    }
  }

  /** @param {Event & { detail?: { ids?: string[], productId?: string | null, added?: boolean } }} event */
  #onChange = (event) => {
    const ids = event.detail?.ids ?? Wishlist.getIds();
    const removed = this.#rendered.filter((id) => !ids.includes(id));
    const added = ids.some((id) => !this.#rendered.includes(id));

    if (added) {
      // A piece was saved in another tab: reload the grid.
      this.#load();
      return;
    }

    for (const id of removed) {
      const item = this.querySelector(`[data-product-card-id="${CSS.escape(id)}"]`);
      if (!(item instanceof HTMLElement)) continue;
      const finish = () => {
        item.remove();
        if (!this.querySelector('[data-product-card-id]')) this.#renderEmpty();
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
      } else {
        item.toggleAttribute('data-removing', true);
        window.setTimeout(finish, 260);
      }
    }

    this.#rendered = this.#rendered.filter((id) => ids.includes(id));
    this.#updateCount();
  };

  #renderEmpty() {
    const list = this.#list;
    if (list) {
      list.replaceChildren();
      list.removeAttribute('aria-busy');
      list.hidden = true;
    }
    const empty = this.querySelector('[data-favorites-empty]');
    if (empty instanceof HTMLElement) empty.hidden = false;
    this.#rendered = [];
    this.dataset.state = 'empty';
    this.#updateCount();
  }

  #updateCount() {
    const count = this.#rendered.length;
    const label = this.querySelector('[data-favorites-count]');
    if (label) label.textContent = count === 0 ? '' : count === 1 ? '1 pieza guardada' : `${count} piezas guardadas`;
  }
}

if (!customElements.get('mineralia-favorites')) {
  customElements.define('mineralia-favorites', MineraliaFavorites);
}
